import { ReactElement, SyntheticEvent, useEffect, useState } from 'react'
import {
  DropdownWrapper,
  EditableDropdown,
  FlexWrapper,
  PageWrapper,
  SectionTitle,
  Input,
  ActionButton,
  Button,
  Loader,
  TostMessage
} from '../../../../components'
import strings from '../../../../locale/en'
import DatePicker from 'react-datepicker'
import { useHistory } from 'react-router-dom'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { sessionTypeList, sessionPlatformList } from './const'
import {
  getAdminList,
  getInstitutes,
  getWebexUsers
} from '../../../../redux/coachingCenter/actions'
import { getInstituteDropdown } from '../../User/helpers'
import { DropdownListProps } from '../../../../components/EditableDropdown/typings'
import {
  getBranchesForCourse,
  getWebexUserByBranch,
  resetBranches
} from '../../../../redux/branch/action'
import {
  getBatchesForCourse,
  resetBatches
} from '../../../../redux/batch/action'
import {
  getAllCoursesByInstitute,
  getChildCourses,
  resetCourses,
  resetSubjects
} from '../../../../redux/course/action'
import {
  getBatchDropdown,
  getBranchDropdown,
  getCoursesDropdown,
  getUserDropdown,
  getWebexUsersDropdown
} from '../../../../helpers'
import AdminType from '../../../../const/admin'
import {
  getZoomUsers,
  resetError,
  resetSession,
  resetValues,
  saveSession,
  updateFormErrors,
  updateFormValues
} from '../../../../redux/session/actions'
import getZoomUsersDropdown from '../../../../helpers/getZoomUsersDropdown'
import { validateRequired } from '../../../../helpers/formValidation'
import ROUTES from '../../../../const/routes'
import { ROLE } from '../../../../helpers/determineMenu'

const CreateSession = (): ReactElement => {
  const {
    isLoading,
    formValues,
    formErrors,
    zoomUsers,
    webexUsers,
    webexUserByBranch,
    institutes,
    courses,
    branches,
    batches,
    subjects,
    teachers,
    error,
    userDetail,
    userRole
  } = useSelector(
    (state: RootState) => ({
      isLoading: state.session.isLoading,
      formValues: state.session.formValues,
      formErrors: state.session.formErrors,
      zoomUsers: state.session.zoomUsers,
      webexUsers: state.coachingCenter.webexUsers,
      webexUserByBranch: state.branch?.webexUserByBranch,
      institutes: state.coachingCenter.institutes,
      courses: state.course.courses,
      branches: state.branch.branches,
      batches: state.batch.batches,
      subjects: state.course.subjects,
      teachers: state.coachingCenter.admin?.adminList,
      error: state.session.error,
      userDetail: state.user?.userInfo?.userDetail,
      userRole: state.user.userInfo?.role
    }),
    shallowEqual
  )

  const dispatch = useDispatch()
  const history = useHistory()
  const isEditMode = history.location.pathname !== ROUTES.ADD_SESSION
  const defaultInstituteId = userDetail?.coachingCenterId || ''

  const {
    sessionName: sessionNameValue = '',
    topicName: topicNameValue = '',
    coachingCentreId,
    courseId,
    startTime: sessionStartTime = '',
    sessionDate = '',
    endTime: sessionEndTime = '',
    platform: platformValue
  } = formValues || {}

  const {
    coachingCentreId: instituteError = '',
    branchId: branchError = '',
    courseId: courseError = '',
    batchId: batchError = '',
    subjectId: subjectError = '',
    teacherId: teacherError = '',
    topicName: topicNameError = '',
    platform: platformError = ''
  } = formErrors || {}

  const [webexBranch, setWebexBranch] = useState(true)
  const [webexWithoutBranch, setWebexWithoutBranch] = useState(true)

  const instituteList = institutes ? getInstituteDropdown(institutes) : []
  const courseList = courses ? getCoursesDropdown(courses) : []
  const branchList = branches ? getBranchDropdown(branches) : []
  const batchList = batches ? getBatchDropdown(batches) : []
  const subjectList = subjects ? getCoursesDropdown(subjects) : []
  const teacherList = teachers ? getUserDropdown(teachers) : []
  const zoomUserList = zoomUsers ? getZoomUsersDropdown(zoomUsers) : []
  const webexUsersList = webexUsers ? getWebexUsersDropdown(webexUsers) : []
  const webexUsersByBranchList = webexUserByBranch
    ? getWebexUsersDropdown(webexUserByBranch)
    : []

  const {
    menu: {
      admin: {
        createSession,
        editSession,
        sessionType,
        sessionName,
        instituteName,
        selectCourse,
        selectBranch,
        selectBatch,
        selectSubject,
        selectTopic,
        selectTeacher,
        selectDate,
        startTime,
        endTime,
        selectPlatform,
        selectZoomUser,
        selectAllotedWebexUser,
        selectCustomWebexUser
      }
    },
    button: { save, back }
  } = strings

  const handleValidate = (key: string) => {
    const error = validateRequired(formValues[key])

    dispatch(
      updateFormErrors({
        key,
        value: error
      })
    )
  }

  const handleInstituteSelect = ({ id }: DropdownListProps) => {
    dispatch(getAllCoursesByInstitute(id))
    dispatch(getAdminList({ coachingCentreId: id, type: AdminType.TEACHER }))
    dispatch(
      updateFormValues({
        key: 'coachingCentreId',
        value: id
      })
    )
    dispatch(resetBranches())
    dispatch(resetBatches())
    dispatch(resetCourses())
  }

  const handleCourseSelect = ({ id }: DropdownListProps) => {
    dispatch(
      getBranchesForCourse({
        coachingCentreId,
        courseId: id
      })
    )
    dispatch(getChildCourses({ courseId: id, type: 'SUBJECT' }))
    dispatch(
      updateFormValues({
        key: 'courseId',
        value: id
      })
    )

    dispatch(resetBatches())
    dispatch(resetBranches())
    dispatch(resetSubjects())
  }

  const handleBranchSelect = ({ id, name }: DropdownListProps) => {
    dispatch(
      getBatchesForCourse({
        coachingCentreId,
        courseId,
        branchId: id
      })
    )

    dispatch(getWebexUserByBranch(id))

    dispatch(
      updateFormValues({
        key: 'branchId',
        value: [{ value: id, label: name }]
      })
    )
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    dispatch(saveSession(isEditMode))
  }

  useEffect(() => {
    if (userRole === ROLE.INSTITUTE_ADMIN) {
      dispatch(getAllCoursesByInstitute(defaultInstituteId))
      dispatch(
        getAdminList({
          coachingCentreId: defaultInstituteId,
          type: AdminType.TEACHER
        })
      )
      dispatch(
        updateFormValues({
          key: 'coachingCentreId',
          value: defaultInstituteId
        })
      )
    } else {
      dispatch(getInstitutes())
    }
    dispatch(getZoomUsers())
    dispatch(getWebexUsers())

    return () => {
      dispatch(resetValues())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEditMode) {
      dispatch(getAllCoursesByInstitute(coachingCentreId))
      dispatch(getAdminList({ coachingCentreId, type: AdminType.TEACHER }))
      dispatch(
        getBranchesForCourse({
          coachingCentreId,
          courseId
        })
      )
      dispatch(getChildCourses({ courseId, type: 'SUBJECT' }))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode])

  return isLoading ? (
    <Loader />
  ) : (
    <PageWrapper id="container">
      <SectionTitle
        title={isEditMode ? editSession : createSession}
        hasBorder
      />
      <TostMessage
        show={!!error}
        onCloseHandler={() => dispatch(resetError())}
        message={error}
      />
      <FlexWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={sessionTypeList}
            title={sessionType}
            isDisabled={isEditMode}
            placeholder={sessionType}
            defaultValue={
              isEditMode
                ? { name: formValues.sessionType, id: formValues.sessionType }
                : null
            }
            handleSelect={({ name }) => {
              dispatch(
                updateFormValues({
                  key: 'sessionType',
                  value: name
                })
              )
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <Input
            label={sessionName}
            placeholder={sessionName}
            name="sessionName"
            value={sessionNameValue}
            onChange={(value: string) =>
              dispatch(
                updateFormValues({
                  key: 'sessionName',
                  value
                })
              )
            }
          />
        </DropdownWrapper>
        {userRole !== ROLE.INSTITUTE_ADMIN && (
          <DropdownWrapper>
            <EditableDropdown
              title={instituteName}
              isRequired
              dropdownList={instituteList}
              placeholder={instituteName}
              error={instituteError}
              isDisabled={isEditMode}
              handleSelect={(institue) => handleInstituteSelect(institue)}
              defaultValue={
                isEditMode
                  ? {
                      name: formValues?.coachingCentreId
                        ? instituteList?.filter(
                            (institute) =>
                              institute?.id === formValues?.coachingCentreId
                          )[0]?.name
                        : '',
                      id: formValues.coachingCenterId
                    }
                  : null
              }
            />
          </DropdownWrapper>
        )}
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={courseList}
            title={selectCourse}
            isDisabled={isEditMode}
            placeholder={selectCourse}
            isRequired
            error={courseError}
            defaultValue={
              isEditMode
                ? {
                    name: formValues?.courseId
                      ? courseList?.filter(
                          (course) => course?.id === formValues.courseId
                        )[0]?.name
                      : '',
                    id: formValues.courseId
                  }
                : null
            }
            handleSelect={(course) => handleCourseSelect(course)}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={branchList}
            title={selectBranch}
            placeholder={selectBranch}
            isRequired
            error={branchError}
            handleSelect={(branch) => handleBranchSelect(branch)}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={batchList}
            title={selectBatch}
            placeholder={selectBatch}
            isRequired
            error={batchError}
            handleSelect={({ id, name }) =>
              dispatch(
                updateFormValues({
                  key: 'batchId',
                  value: [{ value: id, label: name }]
                })
              )
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={subjectList}
            title={`${selectSubject}`}
            placeholder={selectSubject}
            isRequired
            error={subjectError}
            defaultValue={{
              id: formValues.subjectId || '',
              name: formValues?.subjectId
                ? subjectList?.filter(
                    (subject) => subject?.id === formValues?.subjectId
                  )[0]?.name
                : ''
            }}
            handleSelect={({ id }) =>
              dispatch(
                updateFormValues({
                  key: 'subjectId',
                  value: id
                })
              )
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <Input
            label={selectTopic}
            value={topicNameValue}
            isRequired
            onBlur={() => handleValidate('topicName')}
            error={topicNameError}
            placeholder={selectTopic}
            onChange={(topicName: string) =>
              dispatch(
                updateFormValues({
                  key: 'topicName',
                  value: topicName
                })
              )
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={teacherList}
            title={selectTeacher}
            placeholder={selectTeacher}
            isRequired
            error={teacherError}
            defaultValue={
              isEditMode
                ? {
                    name: formValues?.teacherId
                      ? teacherList?.filter(
                          (teacher) => teacher?.id === formValues.teacherId
                        )[0]?.name
                      : '',
                    id: formValues.teacherId
                  }
                : null
            }
            handleSelect={({ id }) =>
              dispatch(
                updateFormValues({
                  key: 'teacherId',
                  value: id
                })
              )
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <DatePicker
            selected={sessionDate}
            onSelect={(date: Date) => {
              dispatch(
                updateFormValues({
                  key: 'sessionDate',
                  value: date
                })
              )
            }}
            onChange={(date: Date) => {
              dispatch(
                updateFormValues({
                  key: 'sessionDate',
                  value: date
                })
              )
            }}
            placeholderText={selectDate}
            customInput={
              <Input
                value={sessionDate}
                inputType="text"
                label={selectDate}
                placeholder={selectDate}
                suffix={['far', 'calendar']}
              />
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <DatePicker
            selected={sessionStartTime}
            onSelect={(time: Date) => {
              dispatch(
                updateFormValues({
                  key: 'startTime',
                  value: time
                })
              )
            }}
            onChange={(time: Date) => {
              dispatch(
                updateFormValues({
                  key: 'startTime',
                  value: time
                })
              )
            }}
            showTimeSelectOnly
            showTimeSelect
            timeIntervals={30}
            timeCaption="Time"
            dateFormat="h:mm aa"
            popperPlacement="bottom"
            placeholderText={startTime}
            customInput={
              <Input
                value={sessionStartTime}
                inputType="text"
                label={startTime}
                placeholder={startTime}
                suffix={['far', 'clock']}
              />
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <DatePicker
            selected={sessionEndTime}
            onSelect={(time: Date) => {
              dispatch(
                updateFormValues({
                  key: 'endTime',
                  value: time
                })
              )
            }}
            onChange={(time: Date) => {
              dispatch(
                updateFormValues({
                  key: 'endTime',
                  value: time
                })
              )
            }}
            showTimeSelectOnly
            showTimeSelect
            timeIntervals={30}
            timeCaption="Time"
            dateFormat="h:mm aa"
            popperPlacement="bottom"
            placeholderText={endTime}
            customInput={
              <Input
                value={sessionEndTime}
                inputType="text"
                label={endTime}
                placeholder={endTime}
                suffix={['far', 'clock']}
              />
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            dropdownList={sessionPlatformList}
            title={selectPlatform}
            placeholder={selectPlatform}
            isRequired
            error={platformError}
            handleSelect={({ name }) => {
              dispatch(
                updateFormValues({
                  key: 'platform',
                  value: name
                })
              )
            }}
            isDisabled={isEditMode}
            defaultValue={
              isEditMode
                ? { name: formValues?.platform, id: formValues?.platform }
                : null
            }
          />
        </DropdownWrapper>
        {platformValue === 'ZOOM' && (
          <DropdownWrapper>
            <EditableDropdown
              dropdownList={zoomUserList}
              title={selectZoomUser}
              placeholder={selectZoomUser}
              isDisabled={isEditMode}
              defaultValue={{
                id: formValues.zoomUserId || '',
                name: formValues?.zoomUserId
                  ? zoomUserList?.filter(
                      (zoomUser) => zoomUser?.id === formValues?.zoomUserId
                    )[0]?.name
                  : ''
              }}
              handleSelect={({ id }) => {
                dispatch(
                  updateFormValues({
                    key: 'zoomUserId',
                    value: id
                  })
                )
              }}
            />
          </DropdownWrapper>
        )}
        {platformValue === 'WEBEX' && (
          <>
            {webexBranch && (
              <DropdownWrapper>
                <EditableDropdown
                  dropdownList={webexUsersByBranchList}
                  title={selectAllotedWebexUser}
                  placeholder={selectAllotedWebexUser}
                  handleSelect={({ id }) => {
                    setWebexWithoutBranch(false)
                    dispatch(
                      updateFormValues({
                        key: 'webExUserId',
                        value: id
                      })
                    )
                  }}
                />
              </DropdownWrapper>
            )}
            {webexWithoutBranch && (
              <DropdownWrapper>
                <EditableDropdown
                  dropdownList={webexUsersList}
                  title={selectCustomWebexUser}
                  placeholder={selectCustomWebexUser}
                  handleSelect={({ id }) => {
                    setWebexBranch(false)
                    dispatch(
                      updateFormValues({
                        key: 'webExUserId',
                        value: id
                      })
                    )
                  }}
                />
              </DropdownWrapper>
            )}
          </>
        )}
      </FlexWrapper>
      <FlexWrapper justifyContent="center">
        <ActionButton
          variant="outline-secondary"
          onClick={() => {
            dispatch(resetSession())
            history.goBack()
          }}
        >
          {back}
        </ActionButton>
        <Button
          onClick={(e: SyntheticEvent) => {
            dispatch(resetSession())
            handleSubmit(e)
          }}
        >
          {save}
        </Button>
      </FlexWrapper>
    </PageWrapper>
  )
}

export default CreateSession
